import { useMemo, useState } from "react";
import { categories, feedItems } from "@/mocks/content";

type FeedSectionProps = {
  keyword: string;
};

export default function FeedSection({ keyword }: FeedSectionProps) {
  const [active, setActive] = useState("全部");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = feedItems;
    if (active !== "全部") {
      list = list.filter((i) => i.category === active);
    }
    if (keyword) {
      const k = keyword.toLowerCase();
      list = list.filter(
        (i) =>
          i.title.toLowerCase().includes(k) ||
          i.summary.toLowerCase().includes(k) ||
          i.tag.toLowerCase().includes(k)
      );
    }
    return list;
  }, [active, keyword]);

  return (
    <section id="feed" className="scroll-mt-16 bg-background-100/60">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="mb-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-500">Hot Feed</p>
            <h2 className="font-display mt-2 text-3xl text-foreground-950 md:text-4xl">
              {keyword ? `“${keyword}”的搜索结果` : "热点速递"}
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              {keyword
                ? `共为你找到 ${filtered.length} 条相关内容`
                : `当前 ${active}栏目，共 ${filtered.length} 条新瓜`}
            </p>
          </div>

          {/* category segmented control */}
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setActive(c.key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active === c.key
                    ? "bg-primary-500 font-medium text-background-50"
                    : "bg-background-50 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* feed grid */}
        {filtered.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((item) => (
              <article
                key={item.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-50 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="relative h-44 w-full overflow-hidden md:h-48">
                  <img
                    src={item.image}
                    alt={item.title}
                    title={`${item.title} 娱乐热点资讯`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {item.hot && (
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                      <i className="ri-fire-fill"></i>爆
                    </span>
                  )}
                  <span className="absolute right-3 top-3 rounded-full bg-foreground-950/60 px-2.5 py-1 text-xs text-background-50 backdrop-blur-sm">
                    {item.tag}
                  </span>
                </div>

                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <div className="mb-2 flex items-center gap-2 text-xs text-foreground-500">
                    <span className="font-medium text-secondary-600">{item.category}</span>
                    <span className="h-1 w-1 rounded-full bg-background-300"></span>
                    <span>{item.time}</span>
                  </div>
                  <h3 className="text-base font-bold leading-snug text-foreground-950 line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600 line-clamp-2">
                    {item.summary}
                  </p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-200/70 pt-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {item.views}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-message-3-line"></i>
                      {item.comments}
                    </span>
                    <a href="#feed" className="font-medium text-primary-600 hover:underline">
                      围观全文
                      <i className="ri-arrow-right-line ml-0.5"></i>
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <i className="ri-search-eye-line text-5xl text-background-400"></i>
            <p className="mt-4 text-base text-foreground-600">暂时没有搜到相关内容</p>
            <p className="mt-1 text-sm text-foreground-400">换个关键词，或切换栏目试试看吧</p>
          </div>
        )}

        {/* toggle load more (demonstration) */}
        {filtered.length > 0 && (
          <div className="mt-10 text-center">
            {open ? (
              <p className="text-sm text-foreground-500">
                已加载全部内容，稍后会有更多新鲜瓜点送达
                <i className="ri-emoji-sticker-line ml-1"></i>
              </p>
            ) : (
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-loop-left-line"></i>
                滚动刷新查看更多
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}